import React from 'react';
import { useForm } from 'react-final-form';
import { ListingFormState } from 'ha/pages/ListingEdit/types';
import { useTrackEvent } from 'ha/modules/Analytics/helpers/TrackEvent';
import { v4 as generateId } from 'uuid';

import { ListingMultiUnit, AliasType } from '../types';

enum SuffixEventNames {
  'descending' = 'Numeric Descending',
  'ascending' = 'Numeric Ascending',
  'alphabetical' = 'Alphabetic',
  'undefined' = 'No Suffix',
}

const generateUnits = (
  numberOfUnitsToAdd: number,
  availableUnits: ListingMultiUnit[],
): ListingMultiUnit[] => {
  let lastAvailableUnitLength = availableUnits.length;
  let newUnits: ListingMultiUnit[] = [];
  for (let index = 0; index < numberOfUnitsToAdd; index += 1) {
    const currentAvailableUnitId = lastAvailableUnitLength + 1;

    newUnits = [
      ...newUnits,
      {
        alias: `Unit ${currentAvailableUnitId}`,
        newAlias: '',
        toDelete: false,
        tempId: generateId(),
      },
    ];
    lastAvailableUnitLength = currentAvailableUnitId;
  }

  return newUnits;
};

interface ParentFormState {
  multiUnits: ListingMultiUnit[];
}
const useManageUnits = (
  values: ListingFormState & {
    units?: ListingMultiUnit[];
  },
) => {
  const hasUnits = values?.units?.length && values?.units?.length > 1;

  const [units, setUnits] = React.useState<ListingMultiUnit[]>(
    values.units || [],
  );
  const [isInitialState, setIsInitialState] = React.useState(!hasUnits);
  //Initial State is when user just clicked on the MUL option
  const [numberOfUnitsToAdd, setNumberOfUnitsToAdd] = React.useState<number>(
    units.length < 2 ? 2 : 1,
  );
  const [showAddUnitsModal, setShowAddUnitsModal] = React.useState<boolean>(
    !hasUnits,
  );
  const [showRemoveUnitModal, setShowRemoveUnitModal] =
    React.useState<boolean>(false);

  const addMoreUnits = React.useCallback(() => {
    const newUnits: ListingMultiUnit[] = generateUnits(
      isInitialState
        ? Number(numberOfUnitsToAdd) - 1
        : Number(numberOfUnitsToAdd),
      units,
    );
    if (isInitialState && units[0].alias === '') {
      setUnits([
        {
          ...units[0],
          alias: 'Unit 1',
          newAlias: 'Unit 1',
          toUpdate: true,
          aliasType: AliasType.GeneralAlias,
        },
      ]);
    }
    setUnits(state => [...state, ...newUnits]);
    setShowAddUnitsModal(false);
    setIsInitialState(false);
  }, [numberOfUnitsToAdd, setUnits, units, setShowAddUnitsModal]);

  const form = useForm<ParentFormState>();
  const trackEvent = useTrackEvent();

  React.useEffect(() => {
    form.change('multiUnits', units);
  }, [form, units]);

  const deleteUnit = React.useCallback(
    (unit: ListingMultiUnit) => {
      if (unit.unitId) {
        setUnits(state =>
          state.map(item => {
            if (item.unitId === unit.unitId) return { ...item, toDelete: true };
            return item;
          }),
        );
      } else {
        setUnits(state =>
          state.filter(stateUnit => stateUnit.alias !== unit.alias),
        );
      }
    },
    [setUnits],
  );

  function convertToNumberingScheme(index: number) {
    const baseChar = 'A'.charCodeAt(0);
    let num = index;
    let letters = '';

    do {
      num -= 1;
      letters = String.fromCharCode(baseChar + (num % 26)) + letters;
      num = Math.floor(num / 26); // quick `floor`
    } while (num > 0);

    return letters;
  }

  const updateUnitAlias = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, unit: ListingMultiUnit) => {
      e.persist();
      if (unit.unitId) {
        setUnits(state =>
          state.map(item => {
            if (item.unitId === unit.unitId)
              return {
                ...item,
                alias: e.target.value,
                newAlias: e.target.value,
                toUpdate: true,
                aliasType: AliasType.SpecificAlias,
              };
            return item;
          }),
        );
      } else {
        setUnits(state =>
          state.map(item => {
            if (unit.tempId === item.tempId)
              return {
                ...item,
                alias: e.target.value,
                newAlias: e.target.value,
                toUpdate: false,
                aliasType: AliasType.SpecificAlias,
              };
            return item;
          }),
        );
      }
    },
    [],
  );

  const renameAllUnits = React.useCallback(() => {
    trackEvent('Clicked Apply suffix', {
      Suffix: SuffixEventNames[`${values.roomNameSuffix}`],
    });

    const roomNamePrefix = !values.roomNamePrefix ? '' : values.roomNamePrefix;

    switch (values.roomNameSuffix) {
      case 'ascending':
        setUnits(state =>
          state.map((stateUnit, index) => {
            return {
              ...stateUnit,
              alias: `${roomNamePrefix} ${index + 1}`,
              newAlias: `${roomNamePrefix} ${index + 1}`,
              toUpdate: !!stateUnit.unitId,
              aliasType: AliasType.GeneralAlias,
            };
          }),
        );
        break;
      case 'descending':
        setUnits(state =>
          state.map((stateUnit, index) => {
            return {
              ...stateUnit,
              alias: `${roomNamePrefix} ${units.length - index}`,
              newAlias: `${roomNamePrefix} ${units.length - index}`,
              toUpdate: !!stateUnit.unitId,
              aliasType: AliasType.GeneralAlias,
            };
          }),
        );
        break;
      case 'alphabetical':
        setUnits(state =>
          state.map((stateUnit, index) => {
            return {
              ...stateUnit,
              alias: `${roomNamePrefix} ${convertToNumberingScheme(index + 1)}`,
              newAlias: `${roomNamePrefix} ${convertToNumberingScheme(
                index + 1,
              )}`,
              toUpdate: !!stateUnit.unitId,
              aliasType: AliasType.GeneralAlias,
            };
          }),
        );
        break;
      default:
        break;
    }
  }, [trackEvent, values.roomNameSuffix, values.roomNamePrefix, units.length]);

  return {
    addMoreUnits,
    updateUnitAlias,
    deleteUnit,
    numberOfUnitsToAdd,
    setNumberOfUnitsToAdd,
    renameAllUnits,
    units: units.filter(unit => !unit.toDelete),
    showAddUnitsModal,
    setShowAddUnitsModal,
    showRemoveUnitModal,
    setShowRemoveUnitModal,
    isInitialState,
    setIsInitialState,
  };
};

export { useManageUnits };
